// Golf scoring helpers

// Handicap strokes received on a given hole based on stroke index (SI 1-18)
export function strokesReceived(handicap, si) {
  const h = Math.max(0, Math.round(handicap || 0));
  const base = Math.floor(h / 18);
  const extra = si <= (h % 18) ? 1 : 0;
  return base + extra;
}

// Stableford points: max(0, 2 - (net_strokes - par)), only if strokes given
export function stablefordPoints(strokes, par, handicap, si) {
  if (!strokes || strokes <= 0) return 0;
  const received = strokesReceived(handicap, si);
  const net = strokes - received;
  const pts = 2 - (net - par);
  return Math.max(0, pts);
}

// Totals for a player across 18 holes on a course
// Handicap a player competed off on a given day (snapshotted when the day is created/scored, so later edits don't rewrite history)
export function dayHandicap(day, player) {
  const h = day?.handicaps?.[player?.id];
  return typeof h === "number" ? h : (player?.handicap || 0);
}

export function playerDayTotals(player, group, course, day = null) {
  const hcp = dayHandicap(day, player);
  const scores = (group && group.scores && group.scores[player.id]) || [];
  let strokes = 0;
  let points = 0;
  let holesPlayed = 0;
  let fhHit = 0;
  let fhAttempts = 0; // par >= 4 holes with strokes recorded
  let girHit = 0;
  let girHoles = 0; // holes with strokes recorded
  let putts = 0;
  let puttHoles = 0;
  let onePutts = 0;
  let threePutts = 0;
  let fhRecorded = 0; // holes where fairway was explicitly marked
  for (let i = 0; i < 18; i++) {
    const s = scores[i];
    if (s && s.strokes) {
      if (typeof s.fairway === "boolean") fhRecorded += 1;
      strokes += s.strokes;
      points += stablefordPoints(s.strokes, course.par[i], hcp, course.si[i]);
      holesPlayed += 1;
      girHoles += 1;
      if (s.gir === true) girHit += 1;
      if (course.par[i] >= 4) {
        fhAttempts += 1;
        if (s.fairway === true) fhHit += 1;
      }
      if (typeof s.putts === "number") { putts += s.putts; puttHoles += 1; if (s.putts === 1) onePutts += 1; if (s.putts >= 3) threePutts += 1; }
    }
  }
  return { strokes, points, holesPlayed, fhHit, fhAttempts, fhRecorded, girHit, girHoles, putts, puttHoles, onePutts, threePutts };
}

// Compute Stableford leaderboard for a day (across all groups)
export function dayLeaderboard(day, event) {
  const course = event.courses[day.courseKey];
  if (!course) return [];
  const rows = [];
  for (const group of day.groups || []) {
    for (const pid of group.playerIds || []) {
      const player = event.players.find((p) => p.id === pid);
      if (!player) continue;
      const totals = playerDayTotals(player, group, course, day);
      rows.push({ player, ...totals });
    }
  }
  rows.sort((a, b) => b.points - a.points || a.strokes - b.strokes);
  return rows;
}

// Compute Champion points for a finalized day.
// Points scheme: player count -> descending array; ties get average.
export function championPointsForDay(day, event) {
  const rows = dayLeaderboard(day, event);
  const n = rows.length;
  if (n === 0) return [];
  // Award n, n-1, ... 1 (only if fully played? We'll award regardless)
  // Group rows by points to handle ties
  const results = [];
  let i = 0;
  while (i < n) {
    let j = i;
    while (j < n && rows[j].points === rows[i].points && rows[j].strokes === rows[i].strokes) j++;
    // Ranks from i..j-1
    const ranks = [];
    for (let k = i; k < j; k++) ranks.push(n - k); // rank i -> n, next -> n-1
    const avg = ranks.reduce((a, b) => a + b, 0) / ranks.length;
    for (let k = i; k < j; k++) {
      results.push({ playerId: rows[k].player.id, championPoints: avg, points: rows[k].points, strokes: rows[k].strokes });
    }
    i = j;
  }
  return results;
}

// Champion cumulative leaderboard across all finalized days
function _emptyTotals(player) {
  return {
    player, championPoints: 0, daysPlayed: 0,
    fhHit: 0, fhAttempts: 0, girHit: 0, girHoles: 0,
    putts: 0, puttHoles: 0, onePutts: 0, threePutts: 0,
    strokes: 0, points: 0, holesPlayed: 0,
    dayWins: 0,
  };
}

function _addDayWins(totals, day, event) {
  const dayRows = dayLeaderboard(day, event);
  if (!dayRows.length) return;
  const top = dayRows[0];
  for (const row of dayRows) {
    const isWinner = row.points === top.points && row.strokes === top.strokes && row.points > 0;
    if (isWinner && totals[row.player.id]) totals[row.player.id].dayWins += 1;
  }
}

function _addPlayerStatsForDay(totals, day, course, event) {
  for (const group of day.groups || []) {
    for (const pid of group.playerIds || []) {
      const player = event.players.find((p) => p.id === pid);
      if (!player || !totals[pid]) continue;
      const t = playerDayTotals(player, group, course, day);
      const bucket = totals[pid];
      bucket.fhHit += t.fhHit;
      bucket.fhAttempts += t.fhAttempts;
      bucket.girHit += t.girHit;
      bucket.girHoles += t.girHoles;
      bucket.putts += t.putts;
      bucket.puttHoles += t.puttHoles;
      bucket.onePutts += t.onePutts;
      bucket.threePutts += t.threePutts;
      bucket.strokes += t.strokes;
      bucket.points += t.points;
      bucket.holesPlayed += t.holesPlayed;
    }
  }
}

export function championLeaderboard(event) {
  const totals = {};
  for (const player of event.players) totals[player.id] = _emptyTotals(player);

  for (const day of event.days || []) {
    if (!day.finalized) continue;
    const res = day.results && day.results.length ? day.results : championPointsForDay(day, event);
    for (const r of res) {
      if (!totals[r.playerId]) continue;
      totals[r.playerId].championPoints += r.championPoints;
      totals[r.playerId].daysPlayed += 1;
    }
    if (res.length) _addDayWins(totals, day, event);
    const course = event.courses[day.courseKey];
    if (course) _addPlayerStatsForDay(totals, day, course, event);
  }
  return Object.values(totals).sort((a, b) => b.championPoints - a.championPoints);
}

// BBB Leaderboard: count of bingo/bango/bongo per player, day-wise or event-wise
export function bbbLeaderboard(event, dayId = null) {
  const totals = {};
  for (const player of event.players) totals[player.id] = { player, bingo: 0, bango: 0, bongo: 0, total: 0 };
  const days = dayId ? (event.days || []).filter((d) => d.id === dayId) : (event.days || []);
  for (const day of days) {
    if (!day.bbbEnabled) continue;
    for (const group of day.groups || []) {
      for (const entry of group.bbb || []) {
        if (!entry) continue;
        if (entry.bingo && totals[entry.bingo]) totals[entry.bingo].bingo += 1;
        if (entry.bango && totals[entry.bango]) totals[entry.bango].bango += 1;
        if (entry.bongo && totals[entry.bongo]) totals[entry.bongo].bongo += 1;
      }
    }
  }
  Object.values(totals).forEach((t) => { t.total = t.bingo + t.bango + t.bongo; });
  return Object.values(totals).sort((a, b) => b.total - a.total);
}

// Skins side game — compute per-day.
// day.skinsMode: "off" | "gross" | "net" (default off)
// Rules: lowest score on a hole banks the skin (+ any carry from prior tied holes). Ties roll carry to next hole.
// A hole is only judged when EVERY competing player has a stroke count.
// Returns { holes: [{holeIdx, decided, winnerId|null, value, carry, tied}], totals: {playerId: skinsWon}, unresolvedCarry }
export function daySkins(day, event) {
  const mode = day?.skinsMode || "off";
  if (mode === "off") return null;
  const course = event.courses[day.courseKey];
  if (!course) return null;
  // Collect all players competing (all players assigned to any group on the day)
  const competingIds = new Set();
  for (const g of day.groups || []) for (const pid of g.playerIds || []) competingIds.add(pid);
  const players = event.players.filter((p) => competingIds.has(p.id));
  if (players.length < 2) return null;
  // Build a helper: get stroke for player on hole i
  const getStroke = (pid, i) => {
    for (const g of day.groups || []) {
      if ((g.playerIds || []).includes(pid)) {
        return g.scores?.[pid]?.[i]?.strokes || null;
      }
    }
    return null;
  };
  const totals = {};
  for (const p of players) totals[p.id] = 0;
  const holes = [];
  let carry = 0;
  for (let i = 0; i < 18; i++) {
    const par = course.par[i];
    const si = course.si[i];
    // Every competing player must have a stroke; otherwise hole is undecided (do NOT advance carry)
    let allScored = true;
    let scores = [];
    for (const p of players) {
      const s = getStroke(p.id, i);
      if (!s) { allScored = false; break; }
      const value = mode === "net" ? s - strokesReceived(dayHandicap(day, p), si) : s;
      scores.push({ pid: p.id, value });
    }
    if (!allScored) {
      holes.push({ holeIdx: i, decided: false, winnerId: null, value: 1 + carry, carry, tied: false, par });
      continue;
    }
    scores.sort((a, b) => a.value - b.value);
    const lowest = scores[0].value;
    const lowestPlayers = scores.filter((s) => s.value === lowest);
    if (lowestPlayers.length === 1) {
      const winnerId = lowestPlayers[0].pid;
      const value = 1 + carry;
      totals[winnerId] += value;
      holes.push({ holeIdx: i, decided: true, winnerId, value, carry, tied: false, par });
      carry = 0;
    } else {
      carry += 1;
      holes.push({ holeIdx: i, decided: true, winnerId: null, value: 0, carry: carry - 1, tied: true, par });
    }
  }
  const rows = players
    .map((p) => ({ player: p, skins: totals[p.id] || 0, money: (totals[p.id] || 0) * (day.skinsValue || 0) }))
    .sort((a, b) => b.skins - a.skins);
  return { holes, totals, rows, unresolvedCarry: carry, mode, value: day.skinsValue || 0 };
}

// Trip skins leaderboard: sum skins across all days with skins enabled
export function tripSkinsLeaderboard(event) {
  const totals = {};
  for (const p of event.players) totals[p.id] = { player: p, skins: 0, money: 0, days: 0 };
  for (const day of event.days || []) {
    const res = daySkins(day, event);
    if (!res) continue;
    const value = day.skinsValue || 0;
    for (const pid in res.totals) {
      if (totals[pid]) {
        const won = res.totals[pid];
        totals[pid].skins += won;
        totals[pid].money += won * value;
        if (won > 0) totals[pid].days += 1;
      }
    }
  }
  return Object.values(totals).sort((a, b) => b.skins - a.skins);
}

// Nassau side game — three separate bets per day: Front 9 low total, Back 9 low total, Overall 18 low total.
// day.nassauMode: "off" | "gross" | "net"; day.nassauValue: $ per segment
// Ties split evenly. Returns { segments: {front:{winners,value,leader,complete}, back:{...}, overall:{...}}, rows: [{player,segmentsWon,money}], value }
export function dayNassau(day, event) {
  const mode = day?.nassauMode || "off";
  if (mode === "off") return null;
  const course = event.courses[day.courseKey];
  if (!course) return null;
  const competingIds = new Set();
  for (const g of day.groups || []) for (const pid of g.playerIds || []) competingIds.add(pid);
  const players = event.players.filter((p) => competingIds.has(p.id));
  if (players.length < 2) return null;
  const getStroke = (pid, i) => {
    for (const g of day.groups || []) {
      if ((g.playerIds || []).includes(pid)) return g.scores?.[pid]?.[i]?.strokes || null;
    }
    return null;
  };
  const totalFor = (pid, start, end) => {
    let sum = 0; let complete = true;
    for (let i = start; i < end; i++) {
      const s = getStroke(pid, i);
      if (!s) { complete = false; break; }
      const val = mode === "net" ? s - strokesReceived(dayHandicap(day, event.players.find(p => p.id === pid)), course.si[i]) : s;
      sum += val;
    }
    return { sum, complete };
  };
  const segment = (start, end) => {
    const rows = players.map((p) => ({ pid: p.id, ...totalFor(p.id, start, end) }));
    if (rows.some((r) => !r.complete)) return { complete: false, winners: [], leader: null, rows };
    rows.sort((a, b) => a.sum - b.sum);
    const low = rows[0].sum;
    const winners = rows.filter((r) => r.sum === low).map((r) => r.pid);
    return { complete: true, winners, leader: rows[0].pid, rows };
  };
  const value = day.nassauValue || 0;
  const segments = {
    front: segment(0, 9),
    back: segment(9, 18),
    overall: segment(0, 18),
  };
  const perPlayer = {};
  for (const p of players) perPlayer[p.id] = { player: p, segmentsWon: 0, money: 0 };
  for (const seg of Object.values(segments)) {
    if (!seg.complete || seg.winners.length === 0) continue;
    const share = 1 / seg.winners.length;
    for (const pid of seg.winners) {
      perPlayer[pid].segmentsWon += share;
      perPlayer[pid].money += share * value;
    }
  }
  const rows = Object.values(perPlayer).sort((a, b) => b.segmentsWon - a.segmentsWon);
  return { segments, rows, value, mode };
}

export function tripNassauLeaderboard(event) {
  const totals = {};
  for (const p of event.players) totals[p.id] = { player: p, segmentsWon: 0, money: 0, days: 0 };
  for (const day of event.days || []) {
    const res = dayNassau(day, event);
    if (!res) continue;
    for (const r of res.rows) {
      if (totals[r.player.id]) {
        totals[r.player.id].segmentsWon += r.segmentsWon;
        totals[r.player.id].money += r.money;
        if (r.segmentsWon > 0) totals[r.player.id].days += 1;
      }
    }
  }
  return Object.values(totals).sort((a, b) => b.money - a.money || b.segmentsWon - a.segmentsWon);
}

// For player card: return per-day breakdown for a single player
// Returns [{ dayId, dayLabel, courseName, par:[], si:[], holes:[{strokes,points,fh,gir,putts,net}], totals: {...} }]
export function playerBreakdown(event, playerId, { onlyDayId = null } = {}) {
  const player = event.players.find((p) => p.id === playerId);
  if (!player) return [];
  const days = (event.days || []).filter((d) => onlyDayId ? d.id === onlyDayId : true);
  const sections = [];
  for (const day of days) {
    const course = event.courses[day.courseKey];
    if (!course) continue;
    const group = (day.groups || []).find((g) => (g.playerIds || []).includes(playerId));
    if (!group) continue;
    const scores = (group.scores && group.scores[playerId]) || [];
    const holes = [];
    let anyPlayed = false;
    for (let i = 0; i < 18; i++) {
      const s = scores[i] || {};
      if (s.strokes) anyPlayed = true;
      const rec = strokesReceived(dayHandicap(day, player), course.si[i]);
      const net = s.strokes ? s.strokes - rec : null;
      const pts = s.strokes ? stablefordPoints(s.strokes, course.par[i], dayHandicap(day, player), course.si[i]) : 0;
      holes.push({
        par: course.par[i],
        si: course.si[i],
        strokes: s.strokes || null,
        net,
        strokesReceived: rec,
        points: pts,
        fh: s.fairway === true,
        fhMarked: s.fairway === true || s.fairway === false,
        gir: s.gir === true,
        girMarked: s.gir === true || s.gir === false,
        putts: typeof s.putts === "number" ? s.putts : null,
      });
    }
    if (!anyPlayed && onlyDayId === null) continue;
    const totals = playerDayTotals(player, group, course, day);
    sections.push({
      dayId: day.id,
      dayLabel: day.label,
      courseName: course.name,
      finalized: !!day.finalized,
      par: course.par,
      si: course.si,
      holes,
      totals,
    });
  }
  return sections;
}

// Nickname-first display name; full name kept as fullName for Setup/edit screens.
export const displayName = (p) => (p?.nickname && p.nickname.trim()) || p?.name || "";
export function withDisplayNames(ev) {
  if (!ev) return ev;
  return { ...ev, players: (ev.players || []).map((p) => ({ ...p, fullName: p.name, name: displayName(p) })) };
}

export function initials(name) {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join("");
}
